import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { EmailImportRow } from '../../types/email.types';

/**
 * Lettura di un file di contatti.
 *
 * Non si usa importUtils perche' quello pretende che le intestazioni del file
 * siano scritte esattamente come le aspetta. Le liste vere arrivano da fiere,
 * gestionali e fogli fatti a mano, con intestazioni ogni volta diverse:
 * qui si prova a indovinarle, e quando non basta si lascia scegliere a mano.
 */

export type CampoContatto = 'email' | 'nome' | 'azienda' | 'telefono' | 'citta' | 'provincia';

/** Quale colonna del file corrisponde a quale campo. null = nessuna. */
export type MappaColonne = Record<CampoContatto, string | null>;

export interface FileLetto {
  intestazioni: string[];
  righe: Record<string, string>[];
}

export interface RigheCostruite {
  valide: EmailImportRow[];
  /** Righe scartate, con il motivo: vanno mostrate, non nascoste. */
  scartate: { riga: number; valore: string; motivo: string }[];
  /** Indirizzi ripetuti dentro lo stesso file. */
  duplicati: number;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Intestazione ridotta all'osso, per poterla confrontare. */
function normalizza(testo: string): string {
  return testo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

const ALIAS: Record<CampoContatto, string[]> = {
  email: ['email', 'mail', 'indirizzoemail', 'indirizzomail', 'postaelettronica', 'emailaddress'],
  nome: ['nome', 'nominativo', 'contatto', 'referente', 'nomeecognome', 'name', 'fullname', 'cognome'],
  azienda: ['azienda', 'ragionesociale', 'societa', 'ditta', 'company', 'cliente', 'impresa'],
  telefono: ['telefono', 'tel', 'cellulare', 'cell', 'phone', 'mobile', 'recapito'],
  citta: ['citta', 'comune', 'city', 'localita'],
  provincia: ['provincia', 'prov', 'pr', 'province'],
};

export function mappaVuota(): MappaColonne {
  return { email: null, nome: null, azienda: null, telefono: null, citta: null, provincia: null };
}

/**
 * Indovina la corrispondenza fra colonne del file e campi.
 * Per l'email, se l'intestazione non dice niente si guarda il contenuto: la
 * colonna con piu' valori che sembrano indirizzi e' quella giusta.
 */
export function indovinaColonne(file: FileLetto): MappaColonne {
  const mappa = mappaVuota();
  const presi = new Set<string>();

  (Object.keys(ALIAS) as CampoContatto[]).forEach((campo) => {
    const trovata = file.intestazioni.find((h) => {
      if (presi.has(h)) return false;
      const n = normalizza(h);
      return ALIAS[campo].some((a) => n === a || n.startsWith(a) || a.startsWith(n));
    });
    if (trovata) {
      mappa[campo] = trovata;
      presi.add(trovata);
    }
  });

  if (!mappa.email) {
    let migliore: { colonna: string; quanti: number } | null = null;
    file.intestazioni.forEach((h) => {
      if (presi.has(h)) return;
      const quanti = file.righe.filter((r) => EMAIL_REGEX.test((r[h] ?? '').trim())).length;
      if (quanti > 0 && (!migliore || quanti > migliore.quanti)) {
        migliore = { colonna: h, quanti };
      }
    });
    if (migliore) mappa.email = (migliore as { colonna: string }).colonna;
  }

  return mappa;
}

export function leggiFile(file: File): Promise<FileLetto> {
  const nome = file.name.toLowerCase();
  if (nome.endsWith('.xlsx') || nome.endsWith('.xls')) return leggiExcel(file);
  return leggiCsv(file);
}

function ripulisci(griglia: unknown[][]): FileLetto {
  if (griglia.length === 0) return { intestazioni: [], righe: [] };

  const intestazioni = (griglia[0] as unknown[]).map((h, i) =>
    String(h ?? '').trim() || `Colonna ${i + 1}`
  );

  const righe = griglia.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    intestazioni.forEach((h, i) => {
      obj[h] = String((r as unknown[])[i] ?? '').trim();
    });
    return obj;
  });

  return { intestazioni, righe: righe.filter((r) => Object.values(r).some((v) => v !== '')) };
}

function leggiCsv(file: File): Promise<FileLetto> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: true,
      // Il separatore lo deduce Papa: i fogli italiani usano spesso il punto e virgola.
      complete: (res) => resolve(ripulisci(res.data as unknown[][])),
      error: (err) => reject(err),
    });
  });
}

function leggiExcel(file: File): Promise<FileLetto> {
  return new Promise((resolve, reject) => {
    const lettore = new FileReader();
    lettore.onerror = () => reject(new Error('Non sono riuscito a leggere il file'));
    lettore.onload = (e) => {
      try {
        const dati = new Uint8Array(e.target?.result as ArrayBuffer);
        const cartella = XLSX.read(dati, { type: 'array' });
        const foglio = cartella.Sheets[cartella.SheetNames[0]];
        const griglia = XLSX.utils.sheet_to_json(foglio, { header: 1, raw: false }) as unknown[][];
        resolve(ripulisci(griglia));
      } catch {
        reject(new Error('Il file non sembra un foglio di calcolo valido'));
      }
    };
    lettore.readAsArrayBuffer(file);
  });
}

/** Trasforma le righe lette nelle righe da mandare al database. */
export function costruisciRighe(file: FileLetto, mappa: MappaColonne): RigheCostruite {
  const valide: EmailImportRow[] = [];
  const scartate: RigheCostruite['scartate'] = [];
  const visti = new Set<string>();
  let duplicati = 0;

  if (!mappa.email) {
    return { valide, scartate: [{ riga: 0, valore: '', motivo: 'Nessuna colonna indicata per l\'email' }], duplicati };
  }

  file.righe.forEach((r, i) => {
    const email = (r[mappa.email as string] ?? '').trim().toLowerCase();
    // +2: la prima riga del file e' l'intestazione, e i fogli contano da 1.
    const numeroRiga = i + 2;

    if (!email) {
      scartate.push({ riga: numeroRiga, valore: '', motivo: 'Manca l\'indirizzo' });
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      scartate.push({ riga: numeroRiga, valore: email, motivo: 'Non sembra un indirizzo valido' });
      return;
    }
    if (visti.has(email)) {
      duplicati += 1;
      return;
    }
    visti.add(email);

    const prendi = (campo: CampoContatto): string | undefined => {
      const colonna = mappa[campo];
      if (!colonna) return undefined;
      return (r[colonna] ?? '').trim() || undefined;
    };

    valide.push({
      email,
      nome: prendi('nome'),
      azienda: prendi('azienda'),
      telefono: prendi('telefono'),
      citta: prendi('citta'),
      provincia: prendi('provincia'),
    });
  });

  return { valide, scartate, duplicati };
}
